import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  Home,
  BookOpen,
  Map,
  ShoppingBag,
  Wallet,
  Camera,
  AlertCircle,
  Menu,
  ChevronRight,
  Sun,
  Cloud,
  CloudRain
} from 'lucide-react';
import { Show, SignInButton, SignUpButton, UserButton, useUser } from "@clerk/react";

// Views
import HomeView from './views/HomeView';
import BookingsView from './views/BookingsView';
import ExploreView from './views/ExploreView';
import MarketplaceView from './views/MarketplaceView';
import WalletView from './views/WalletView';
import MomentsView from './views/MomentsView';
import EmergencyView from './views/EmergencyView';
import AdminView from './views/AdminView';
import OnboardingView from './views/OnboardingView';
import ProviderOnboarding from './views/ProviderOnboarding';
import KYCView from './views/KYCView';
import ProviderDashboard from './views/ProviderDashboard';
import { AppProvider, useApp } from './context/AppContext';
import { useAuth } from '@clerk/react';
import { supabase, clerkAuthStore } from './supabaseClient';

const BottomNav = () => {
  const location = useLocation();
  const navItems = [
    { path: '/', label: 'Home', icon: <Home size={20} /> },
    { path: '/bookings', label: 'Bookings', icon: <BookOpen size={20} /> },
    { path: '/explore', label: 'Explore', icon: <Map size={20} /> },
    { path: '/marketplace', label: 'Concierge', icon: <ShoppingBag size={20} /> },
    { path: '/wallet', label: 'Wallet', icon: <Wallet size={20} /> },
    { path: '/moments', label: 'Moments', icon: <Camera size={20} /> },
  ];

  return (
    <nav className="bottom-nav">
      {navItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`nav-item \${location.pathname === item.path ? 'active' : ''}`}
        >
          {item.icon}
          <span>{item.label}</span>
        </Link>
      ))}
    </nav>
  );
};

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const WeatherWidget = () => {
  const [weather, setWeather] = useState({ temp: 31, icon: 'sunny' });

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch('https://api.open-meteo.com/v1/forecast?latitude=6.5244&longitude=3.3792&current_weather=true');
        if (!res.ok) throw new Error('Weather fetch failed');
        const data = await res.json();
        const temp = Math.round(data.current_weather.temperature);
        const code = data.current_weather.weathercode;
        
        let icon = 'sunny';
        if (code >= 1 && code <= 3) icon = 'cloudy';
        else if (code >= 51 && code <= 67) icon = 'rainy';
        else if (code >= 80 && code <= 99) icon = 'thunder';

        setWeather({ temp, icon });
      } catch (err) {
        console.warn('[WeatherWidget] Failed to fetch weather:', err);
      }
    };
    fetchWeather();
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, []);

  const renderIcon = () => {
    switch (weather.icon) {
      case 'cloudy':
        return <Cloud size={14} color="var(--primary-green)" />;
      case 'rainy':
      case 'thunder':
        return <CloudRain size={14} color="#0284c7" />;
      default:
        return <Sun size={14} color="#eab308" className="animate-spin-slow" />;
    }
  };

  return (
    <div className="weather-widget" style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '6px', 
      background: '#F3F4F6', 
      padding: '4px 10px', 
      borderRadius: '20px', 
      fontSize: '11px', 
      fontWeight: '700',
      color: 'var(--text-dark)',
      border: '1px solid var(--border-gray)'
    }}>
      {renderIcon()}
      <span>{weather.temp}°C • Lagos</span>
    </div>
  );
};

const Header = () => (
  <header className="main-header">
    <div className="logo-section" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span className="logo-text">EZ <span className="logo-accent">Naija</span></span>
      <WeatherWidget />
    </div>
    <div className="header-actions">
      {PUBLISHABLE_KEY && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Show when="signed-out">
            <SignInButton mode="modal">
              <button style={{ 
                background: 'transparent', 
                color: 'var(--primary-green)', 
                border: '1px solid var(--primary-green)', 
                padding: '8px 16px', 
                borderRadius: '20px', 
                fontSize: '13px', 
                fontWeight: '700',
                cursor: 'pointer'
              }}>Log In</button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button style={{ 
                background: 'var(--primary-green)', 
                color: 'white', 
                border: 'none', 
                padding: '8px 16px', 
                borderRadius: '20px', 
                fontSize: '13px', 
                fontWeight: '700',
                cursor: 'pointer'
              }}>Join</button>
            </SignUpButton>
          </Show>
          <Show when="signed-in">
            <UserButton afterSignOutUrl="/" />
          </Show>
        </div>
      )}
      <Link to="/emergency" className="emergency-indicator">
        <AlertCircle size={24} color="var(--emergency-red)" />
      </Link>
      <Link to="/admin" className="admin-trigger">
        <Menu size={24} />
      </Link>
    </div>
  </header>
);


const ClerkSupabaseSync = () => {
  const { getToken, userId, isSignedIn, isLoaded } = useAuth();
  const { user: clerkUser } = useUser();
  
  useEffect(() => {
    if (!isLoaded) return;
    
    // Store Clerk getToken function dynamically
    clerkAuthStore.getToken = getToken;
    
    if (!isSignedIn) {
      console.log('[ClerkSupabaseSync] User signed out, credentials cleared.');
      return;
    }
    
    const checkAndCreateProfile = async () => {
      try {
        console.log('[ClerkSupabaseSync] Checking if profile exists in database for user:', userId);
        const { data: existingProfile, error: selectError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', userId)
          .maybeSingle();
          
        if (selectError) {
          console.error('[ClerkSupabaseSync] Error querying profile:', selectError.message);
          return;
        }
          
        if (!existingProfile) {
          console.log('[ClerkSupabaseSync] Profile not found in database, creating one...');
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              display_name: clerkUser?.fullName || clerkUser?.username || clerkUser?.primaryEmailAddress?.emailAddress || 'New User',
              role: 'customer'
            });
          
          if (insertError) {
            console.error('[ClerkSupabaseSync] Error creating profile:', insertError.message);
          } else {
            console.log('[ClerkSupabaseSync] Profile created successfully for:', userId);
          }
        } else {
          console.log('[ClerkSupabaseSync] Profile verified in database.');
        }
      } catch (err) {
        console.error('[ClerkSupabaseSync] Exception in profile synchronization:', err);
      }
    };
    
    checkAndCreateProfile();
  }, [isLoaded, isSignedIn, getToken, userId, clerkUser]);

  return null;
};

function AppContent() {
  const { user, setUser } = useApp();

  return (
    <div className="app-container">
      {PUBLISHABLE_KEY && <ClerkSupabaseSync />}
      {!user.onboarded && (
        <OnboardingView 
          onComplete={() => {
            localStorage.setItem('ez_onboarded', 'true');
            setUser(prev => ({ ...prev, onboarded: true }));
          }} 
        />
      )}
      <Header />
      <main className="view-content">
        <Routes>
          <Route path="/" element={<HomeView />} />
          <Route path="/bookings" element={<BookingsView />} />
          <Route path="/explore" element={<ExploreView />} />
          <Route path="/marketplace" element={<MarketplaceView />} />
          <Route path="/wallet" element={<WalletView />} />
          <Route path="/moments" element={<MomentsView />} />
          <Route path="/emergency" element={<EmergencyView />} />
          <Route path="/admin" element={<AdminView />} />
          <Route path="/provider-setup" element={<ProviderOnboarding />} />
          <Route path="/kyc" element={<KYCView />} />
          <Route path="/provider-dashboard" element={<ProviderDashboard />} />
        </Routes>
      </main>
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppProvider>
        <AppContent />
        <style dangerouslySetInnerHTML={{
          __html: `
        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 100%;
          max-width: 480px;
          height: 70px;
          background: white;
          border-top: 1px solid var(--border-gray);
          display: flex;
          justify-content: space-around;
          align-items: center;
          padding: 0 10px;
          z-index: 1000;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
        }
        .nav-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-decoration: none;
          color: var(--text-light);
          font-size: 10px;
          gap: 4px;
          transition: 0.2s;
        }
        .nav-item.active {
          color: var(--primary-green);
        }
        .nav-item.active svg {
          transform: translateY(-2px);
          stroke-width: 2.5px;
        }
        .main-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background: white;
          position: sticky;
          top: 0;
          z-index: 100;
          border-bottom: 1px solid var(--border-gray);
        }
        .logo-text {
          font-size: 20px;
          font-weight: 800;
          color: var(--primary-green);
        }
        .logo-accent {
          color: var(--text-dark);
        }
        .header-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }
        .emergency-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spinSlow 12s linear infinite;
        }
      `}} />
      </AppProvider>
    </Router>
  );
}

export default App;
